import type {
  ActivityEntry,
  AiJob,
  AiPreview,
  AiProviders,
  ApiToken,
  ApiTokenCreateBody,
  ApiTokenCreated,
  GrantsBody,
  KeyCreateBody,
  KeyDetail,
  KeyLink,
  KeyLinkBatchResult,
  KeyLinkCandidate,
  KeyListItem,
  KeyPatchBody,
  KeyRenameBody,
  KeySearchHit,
  BundleDiff,
  GlossaryTerm,
  GlossaryTermBody,
  GlossaryTermPatchBody,
  ImportApplyRequest,
  ImportBatch,
  ImportDetail,
  ImportPreview,
  ImportRevertResult,
  Locale,
  LocaleAliases,
  LocaleCreateBody,
  Me,
  MissingKey,
  MissingKeyCreated,
  OpenWork,
  Package,
  PackageCreateBody,
  PackageDetail,
  PackageListItem,
  PackageLocalesBody,
  PackagePatchBody,
  PackageStats,
  PatchResult,
  PhraseSyncConflict,
  PhraseSyncRun,
  PhraseSyncRunDetail,
  PhraseSyncStatus,
  Project,
  ProjectCreateBody,
  ProjectDetail,
  ProjectListItem,
  ProjectLocale,
  ProjectLocalesBody,
  ProjectPackagesBody,
  ProjectPatchBody,
  ProjectStats,
  PublishBody,
  PublishTarget,
  PublishTargetLocation,
  PublishTargetListItem,
  QaResult,
  Release,
  ReleaseDetail,
  SubscriberPublish,
  Suggestion,
  TargetCreateBody,
  TargetPatchBody,
  TranslationChange,
  TranslationKey,
  UserAccount,
  UserIdentity,
  UserCreateBody,
  UserPatchBody,
  VerifyResult,
} from '@greco-gls/shared';

import {
  apiDelete,
  apiDownload,
  apiGet,
  apiGetPaged,
  apiPatch,
  apiPost,
  apiPut,
  type PagedResult,
} from './client';

/**
 * One function per endpoint, named after what it does rather than after its verb (spec §9.2).
 *
 * Nothing here caches, retries or reshapes: TanStack Query owns caching, and reshaping in the API
 * layer is how two screens end up disagreeing about what a project looks like.
 */

// -- access -------------------------------------------------------------------

export const fetchMe = (): Promise<Me> => apiGet('/access/me');

export const fetchUsers = (): Promise<UserAccount[]> => apiGet('/access/users');

/** Name and email behind actor oids; an oid nobody registered (`system:*`, `token:N`) is absent. */
export const fetchIdentities = (oids: readonly string[]): Promise<UserIdentity[]> =>
  apiGet('/access/identities', { oids: oids.join(',') });

export const createUser = (body: UserCreateBody): Promise<UserAccount> =>
  apiPost('/access/users', body);

export const patchUser = (userId: number, body: UserPatchBody): Promise<UserAccount> =>
  apiPatch(`/access/users/${userId}`, body);

export const replaceGrants = (userId: number, body: GrantsBody): Promise<UserAccount> =>
  apiPut(`/access/users/${userId}/grants`, body);

export const fetchApiTokens = (): Promise<ApiToken[]> => apiGet('/access/api-tokens');

export const createApiToken = (body: ApiTokenCreateBody): Promise<ApiTokenCreated> =>
  apiPost('/access/api-tokens', body);

export const revokeApiToken = (tokenId: number): Promise<void> =>
  apiDelete(`/access/api-tokens/${tokenId}`);

// -- locales ------------------------------------------------------------------

export const fetchLocales = (): Promise<Locale[]> => apiGet('/locales');

export const createLocale = (body: LocaleCreateBody): Promise<Locale> => apiPost('/locales', body);

export const replaceLocaleAliases = (code: string, aliases: string[]): Promise<LocaleAliases> =>
  apiPut(`/locales/${code}/aliases`, { aliases });

// -- projects -----------------------------------------------------------------

export const fetchProjects = (): Promise<ProjectListItem[]> => apiGet('/projects');

export const fetchProject = (code: string): Promise<ProjectDetail> => apiGet(`/projects/${code}`);

export const fetchProjectStats = (code: string): Promise<ProjectStats> =>
  apiGet(`/projects/${code}/stats`);

export const createProject = (body: ProjectCreateBody): Promise<Project> =>
  apiPost('/projects', body);

export const patchProject = (code: string, body: ProjectPatchBody): Promise<Project> =>
  apiPatch(`/projects/${code}`, body);

export const replaceProjectLocales = (
  code: string,
  body: ProjectLocalesBody,
): Promise<ProjectLocale[]> => apiPut(`/projects/${code}/locales`, body);

export const replaceProjectPackages = (code: string, body: ProjectPackagesBody): Promise<unknown> =>
  apiPut(`/projects/${code}/packages`, body);

// -- packages -----------------------------------------------------------------

export const fetchPackages = (): Promise<PackageListItem[]> => apiGet('/packages');

export const fetchPackage = (code: string): Promise<PackageDetail> => apiGet(`/packages/${code}`);

export const createPackage = (body: PackageCreateBody): Promise<Package> =>
  apiPost('/packages', body);

export const patchPackage = (code: string, body: PackagePatchBody): Promise<Package> =>
  apiPatch(`/packages/${code}`, body);

export const replacePackageLocales = (
  code: string,
  body: PackageLocalesBody,
): Promise<{ locales: string[] }> => apiPut(`/packages/${code}/locales`, body);

export const fetchPackageStats = (code: string): Promise<PackageStats> =>
  apiGet(`/packages/${code}/stats`);

// -- suggestions --------------------------------------------------------------

/**
 * A package's inbox, or a project's: proposals for keys other projects link from it (ADR 0006).
 * The scope is the owner whose keys the suggestions are about.
 */
export const fetchSuggestions = (
  scope: ScopePath,
  status?: 'open' | 'accepted' | 'rejected',
): Promise<Suggestion[]> => apiGet(`${scope}/suggestions`, { status });

export const acceptSuggestion = (suggestionId: number, note?: string): Promise<Suggestion> =>
  apiPost(`/suggestions/${suggestionId}/accept`, { note });

export const rejectSuggestion = (suggestionId: number, note?: string): Promise<Suggestion> =>
  apiPost(`/suggestions/${suggestionId}/reject`, { note });

// -- activity -----------------------------------------------------------------

export const fetchActivity = (
  scope: ScopePath,
  query: { since?: string; limit?: number } = {},
): Promise<ActivityEntry[]> => apiGet(`${scope}/activity`, query);

// -- attention ----------------------------------------------------------------

/** Open work across every scope the caller can read, one entry per owner and kind (§9.2). */
export const fetchAttention = (): Promise<OpenWork[]> => apiGet('/attention');

// -- keys ---------------------------------------------------------------------

/** Query of `GET {scope}/keys` (spec §7.5). Names match the API's aliases exactly. */
export interface KeyListQuery {
  q?: string;
  prefix?: string;
  tag?: string;
  locale?: string;
  status?: 'empty' | 'machine' | 'draft' | 'reviewed';
  missingIn?: string;
  pluralOnly?: boolean;
  includePackages?: boolean;
  /** Project only: keys of other projects this one links (ADR 0006). */
  includeLinked?: boolean;
  /** Comma-separated locales whose values come back with each item. */
  withValues?: string;
  sort?: 'position' | 'name' | 'updated';
  cursor?: string | null;
  limit?: number;
}

/** `/projects/{code}` or `/packages/{code}` — every key route exists under both (spec §7.5). */
export type ScopePath = `/projects/${string}` | `/packages/${string}`;

export const scopePath = (scopeType: 'project' | 'package', code: string): ScopePath =>
  scopeType === 'project' ? `/projects/${code}` : `/packages/${code}`;

export const fetchKeys = (
  scope: ScopePath,
  query: KeyListQuery,
): Promise<PagedResult<KeyListItem>> =>
  apiGetPaged(`${scope}/keys`, { ...query, cursor: query.cursor ?? undefined });

export const fetchKey = (scope: ScopePath, keyId: number): Promise<KeyDetail> =>
  apiGet(`${scope}/keys/${keyId}`);

/** Query of `GET /search/keys` (spec §7.5A). Names match the API's aliases exactly. */
export interface KeySearchQuery {
  /** At least two characters; the API refuses shorter. */
  q: string;
  /** `key` = names and descriptions only, `value` = values only; omitted searches both. */
  match?: 'key' | 'value';
  ownerType?: 'project' | 'package';
  ownerCode?: string;
  /** Locale whose values are searched; omitted means each owner's source locale. */
  locale?: string;
  cursor?: string | null;
  limit?: number;
}

/** Keys of every project the caller can read and of every package, best match first. */
export const searchKeys = (query: KeySearchQuery): Promise<PagedResult<KeySearchHit>> =>
  apiGetPaged('/search/keys', { ...query, cursor: query.cursor ?? undefined });

export const createKey = (scope: ScopePath, body: KeyCreateBody): Promise<TranslationKey> =>
  apiPost(`${scope}/keys`, body);

export const patchKey = (
  scope: ScopePath,
  keyId: number,
  body: KeyPatchBody,
): Promise<TranslationKey> => apiPatch(`${scope}/keys/${keyId}`, body);

export const renameKey = (
  scope: ScopePath,
  keyId: number,
  body: KeyRenameBody,
): Promise<TranslationKey> => apiPost(`${scope}/keys/${keyId}/rename`, body);

/**
 * `force` deletes a key other projects link — without it the API answers `409 KEY_LINKED` with
 * their names, which is what the confirmation shows.
 */
export const deleteKey = (
  scope: ScopePath,
  keyId: number,
  { hard = false, force = false }: { hard?: boolean; force?: boolean } = {},
): Promise<void> =>
  // An undefined param is left out of the query string, so a plain delete stays `DELETE /keys/1`.
  apiDelete(`${scope}/keys/${keyId}`, {
    hard: hard ? 1 : undefined,
    force: force ? 1 : undefined,
  });

export const addKeyComment = (
  scope: ScopePath,
  keyId: number,
  body: { body: string; locale?: string | null },
): Promise<{ id: number }> => apiPost(`${scope}/keys/${keyId}/comments`, body);

// -- translations -------------------------------------------------------------

/** The editor's save. Atomic across the batch: one stale revision refuses all of it (§7.6). */
export const patchTranslations = (
  scope: ScopePath,
  changes: TranslationChange[],
  source: 'ui' | 'ice' | 'api' = 'ui',
  strict = false,
): Promise<PatchResult> => apiPatch(`${scope}/translations`, { changes, source, strict });

export const reviewTranslations = (
  scope: ScopePath,
  items: { keyId: number; locale: string; pluralForm?: string; context?: string }[],
  status: 'reviewed' | 'draft',
): Promise<PatchResult> => apiPost(`${scope}/translations/review`, { items, status });

export const clearTranslations = (
  scope: ScopePath,
  items: { keyId: number; locale: string; pluralForm?: string; context?: string }[],
): Promise<PatchResult> => apiPost(`${scope}/translations/clear`, { items });

export const runQa = (
  scope: ScopePath,
  body: { locale?: string; keyIds?: number[] },
): Promise<QaResult> => apiPost(`${scope}/translations/qa`, body);

// -- releases and publishing --------------------------------------------------
// Only projects publish (spec §8.4); the calls take the project's scope path.

export const fetchTargets = (scope: ScopePath, includeInactive = false): Promise<PublishTarget[]> =>
  apiGet(`${scope}/targets`, includeInactive ? { includeInactive: 1 } : undefined);

/** Every target of every project, with its project — administrators only. */
export const fetchAllTargets = (includeInactive = false): Promise<PublishTargetListItem[]> =>
  apiGet('/publish-targets', includeInactive ? { includeInactive: 1 } : undefined);

/** Where this deployment would publish the project: its environment, account and folder URL. */
export const fetchTargetLocation = (projectCode: string): Promise<PublishTargetLocation> =>
  apiGet('/publish-targets/location', { projectCode });

export const createTarget = (scope: ScopePath, body: TargetCreateBody): Promise<PublishTarget> =>
  apiPost(`${scope}/targets`, body);

export const patchTarget = (
  scope: ScopePath,
  targetId: number,
  body: TargetPatchBody,
): Promise<PublishTarget> => apiPatch(`${scope}/targets/${targetId}`, body);

export const deleteTarget = (scope: ScopePath, targetId: number): Promise<void> =>
  apiDelete(`${scope}/targets/${targetId}`);

/** 202 with the finished release: publishing is synchronous in V1 (spec §7.9). */
export const publishTarget = (
  scope: ScopePath,
  targetCode: string,
  body: PublishBody,
): Promise<Release> => apiPost(`${scope}/targets/${targetCode}/publish`, body);

export const verifyTarget = (scope: ScopePath, targetCode: string): Promise<VerifyResult> =>
  apiPost(`${scope}/targets/${targetCode}/verify`);

export const fetchReleases = (
  scope: ScopePath,
  query: { target?: string; limit?: number; offset?: number } = {},
): Promise<PagedResult<Release>> => apiGetPaged(`${scope}/releases`, query);

export const fetchRelease = (scope: ScopePath, releaseId: number): Promise<ReleaseDetail> =>
  apiGet(`${scope}/releases/${releaseId}`);

export const rollbackRelease = (
  scope: ScopePath,
  releaseId: number,
  note?: string,
): Promise<Release> => apiPost(`${scope}/releases/${releaseId}/rollback`, note ? { note } : {});

/** What publishing would change, per locale (spec §7.8). */
export const fetchExportDiff = (
  scope: ScopePath,
  query: { target: string; locale?: string },
): Promise<BundleDiff> => apiGet(`${scope}/export/diff`, query);

/** Publish every project subscribed to a package, each under its own rules. No `target`: this
 * deployment's environment, the code of every target it creates (spec §8.4). */
export const publishSubscribers = (
  packageCode: string,
  body: { target?: string; note?: string },
): Promise<SubscriberPublish[]> => apiPost(`/packages/${packageCode}/publish-subscribers`, body);

/** `target` narrows a `release` state to that target's last published files (§7.8). */
export interface BundleQuery {
  state?: 'current' | 'release';
  target?: string;
}

export const downloadBundle = (
  code: string,
  locale: string,
  query: BundleQuery = {},
): Promise<Blob> =>
  apiDownload(`/projects/${code}/bundle`, {
    locale,
    state: query.state ?? 'current',
    target: query.target,
  });

export const downloadArchive = (code: string, query: BundleQuery = {}): Promise<Blob> =>
  apiDownload(`/projects/${code}/export.zip`, {
    state: query.state ?? 'current',
    target: query.target,
  });

// -- missing keys -------------------------------------------------------------

export const fetchMissingKeys = (
  code: string,
  query: { status?: string; limit?: number; offset?: number } = {},
): Promise<PagedResult<MissingKey>> => apiGetPaged(`/projects/${code}/missing-keys`, query);

export const createKeyFromMissing = (
  code: string,
  reportId: number,
  body: { description?: string; sourceValue?: string; isPlural?: boolean } = {},
): Promise<MissingKeyCreated> =>
  apiPost(`/projects/${code}/missing-keys/${reportId}/create-key`, body);

/** Answer a report with the key another project already has (`linkableFrom`, ADR 0006). */
export const linkKeyFromMissing = (code: string, reportId: number): Promise<MissingKeyCreated> =>
  apiPost(`/projects/${code}/missing-keys/${reportId}/link-key`);

export const ignoreMissingKey = (code: string, reportId: number): Promise<MissingKey> =>
  apiPost(`/projects/${code}/missing-keys/${reportId}/ignore`);

// -- key links (ADR 0006) -----------------------------------------------------

export const fetchKeyLinks = (
  code: string,
  query: { q?: string; limit?: number; offset?: number } = {},
): Promise<PagedResult<KeyLink>> => apiGetPaged(`/projects/${code}/key-links`, query);

/** Keys of every other project matching `q`, for the link picker; `linkId` when linked already. */
export const fetchKeyLinkCandidates = (
  code: string,
  query: { q: string; cursor?: string | null; limit?: number },
): Promise<PagedResult<KeyLinkCandidate>> =>
  apiGetPaged(`/projects/${code}/key-links/candidates`, {
    ...query,
    cursor: query.cursor ?? undefined,
  });

/** Link keys of other projects by name; every name reports its own outcome. */
export const createKeyLinks = (code: string, names: string[]): Promise<KeyLinkBatchResult> =>
  apiPost(`/projects/${code}/key-links`, { names });

export const deleteKeyLink = (code: string, linkId: number): Promise<void> =>
  apiDelete(`/projects/${code}/key-links/${linkId}`);

// -- AI -----------------------------------------------------------------------

export const fetchAiProviders = (scope: ScopePath): Promise<AiProviders> =>
  apiGet(`${scope}/ai/providers`);

export const startAiTranslation = (
  scope: ScopePath,
  body: {
    keyIds?: number[];
    locales?: string[];
    onlyEmpty?: boolean;
    overwriteMachine?: boolean;
    provider?: string;
  },
): Promise<AiJob> => apiPost(`${scope}/ai/translate`, body);

export const previewAiTranslation = (
  scope: ScopePath,
  body: { keyIds: number[]; locales: string[]; provider?: string },
): Promise<AiPreview> => apiPost(`${scope}/ai/translate/preview`, body);

export const fetchAiJobs = (
  scope: ScopePath,
  query: { status?: string; limit?: number; offset?: number } = {},
): Promise<PagedResult<AiJob>> => apiGetPaged(`${scope}/ai/jobs`, query);

export const fetchAiJob = (jobId: number): Promise<AiJob> => apiGet(`/ai/jobs/${jobId}`);

export const cancelAiJob = (jobId: number): Promise<AiJob> => apiPost(`/ai/jobs/${jobId}/cancel`);

// -- Phrase sync (ADR 0008) ---------------------------------------------------

export const fetchPhraseSyncStatus = (): Promise<PhraseSyncStatus> =>
  apiGet('/admin/phrase-sync/status');

export const fetchPhraseSyncRuns = (
  query: { limit?: number; offset?: number } = {},
): Promise<PagedResult<PhraseSyncRun>> => apiGetPaged('/admin/phrase-sync', query);

export const fetchPhraseSyncRun = (runId: number): Promise<PhraseSyncRunDetail> =>
  apiGet(`/admin/phrase-sync/${runId}`);

/** `projects` null or empty: every project; otherwise these, plus owners of new keys they link. */
export const startPhraseSync = (projects: string[] | null = null): Promise<PhraseSyncRun> =>
  apiPost('/admin/phrase-sync', projects?.length ? { projects } : undefined);

export const applyPhraseSync = (runId: number): Promise<PhraseSyncRun> =>
  apiPost(`/admin/phrase-sync/${runId}/apply`);

export const cancelPhraseSync = (runId: number): Promise<PhraseSyncRun> =>
  apiPost(`/admin/phrase-sync/${runId}/cancel`);

export const fetchPhraseSyncConflicts = (
  query: { limit?: number; offset?: number } = {},
): Promise<PagedResult<PhraseSyncConflict>> =>
  apiGetPaged('/admin/phrase-sync/conflicts', { status: 'open', ...query });

export const decidePhraseSyncConflict = (
  conflictId: number,
  take: 'phrase' | 'glossa',
): Promise<PhraseSyncConflict> =>
  apiPost(`/admin/phrase-sync/conflicts/${conflictId}/decide`, { take });

// -- glossary -----------------------------------------------------------------

export const fetchGlossary = (
  query: { ownerType?: 'global' | 'project' | 'package'; ownerId?: number } = {},
): Promise<GlossaryTerm[]> => apiGet('/glossary', query);

export const createGlossaryTerm = (body: GlossaryTermBody): Promise<GlossaryTerm> =>
  apiPost('/glossary', body);

export const patchGlossaryTerm = (
  termId: number,
  body: GlossaryTermPatchBody,
): Promise<GlossaryTerm> => apiPatch(`/glossary/${termId}`, body);

export const replaceGlossaryTranslations = (
  termId: number,
  translations: Record<string, string>,
): Promise<GlossaryTerm> => apiPut(`/glossary/${termId}/translations`, { translations });

export const deleteGlossaryTerm = (termId: number): Promise<void> =>
  apiDelete(`/glossary/${termId}`);

// -- imports ------------------------------------------------------------------

/** Multipart: the file plus the locale and the options JSON (spec §7.11A). */
export const previewImport = (
  scope: ScopePath,
  file: File,
  locale: string,
  options: Record<string, unknown>,
): Promise<ImportPreview> => {
  const form = new FormData();
  form.append('file', file);
  form.append('locale', locale);
  form.append('options', JSON.stringify(options));
  return apiPost(`${scope}/imports/preview`, form);
};

/** `settle` subscribes to packages and links keys the preview offered, in the same transaction. */
export const applyImport = (
  scope: ScopePath,
  batchId: number,
  settle?: ImportApplyRequest,
): Promise<ImportBatch> => apiPost(`${scope}/imports/${batchId}/apply`, settle);

export const fetchImports = (
  scope: ScopePath,
  query: { limit?: number; offset?: number } = {},
): Promise<PagedResult<ImportBatch>> => apiGetPaged(`${scope}/imports`, query);

export const fetchImport = (
  scope: ScopePath,
  batchId: number,
  query: { action?: string; limit?: number; offset?: number } = {},
): Promise<ImportDetail> => apiGet(`${scope}/imports/${batchId}`, query);

/** Only the rows the import actually changed — what a reviewer reads before reverting. */
export const fetchImportDiff = (
  scope: ScopePath,
  batchId: number,
  query: { limit?: number; offset?: number } = {},
): Promise<ImportDetail> => apiGet(`${scope}/imports/${batchId}/diff`, query);

export const revertImport = (
  scope: ScopePath,
  batchId: number,
  options: { force?: boolean; dryRun?: boolean } = {},
): Promise<ImportRevertResult> =>
  apiPost(`${scope}/imports/${batchId}/revert${options.dryRun ? '?dryRun=1' : ''}`, {
    force: options.force ?? false,
  });
